import { useState, useEffect } from 'react';
import Page from 'src/components/Page';
import {
    Card, CardContent, Container, Stack, TextField, Typography,
    Button, Box, IconButton, Chip, Avatar, Autocomplete, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions, Drawer, Divider,
    FormHelperText, Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { Add, Edit, Delete, People, LocationOn, Close } from '@mui/icons-material';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import apiInstance from 'src/utils/apiAuth';
import * as constants from 'src/utils/constants';

export default function Teams() {
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [countries, setCountries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, team: null });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [countryError, setCountryError] = useState('');

    const TeamSchema = Yup.object().shape({
        name: Yup.string()
            .min(2, 'Team name must be at least 2 characters')
            .max(50, 'Team name must be at most 50 characters')
            .required('Team name is required'),
        lead: Yup.string()
            .required('Team lead is required'),
        members: Yup.array()
            .min(1, 'At least one team member is required')
            .required('Team members are required'),
        countries: Yup.array().default([]),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            lead: '',
            members: [],
            countries: []
        },
        validationSchema: TeamSchema,
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            setCountryError(''); // Clear any existing country errors
            try {
                // Ensure countries is always an array (empty if not provided)
                const teamData = {
                    ...values,
                    countries: Array.isArray(values.countries) ? values.countries : []
                };
                
                if (editingTeam) {
                    await updateTeam(editingTeam.id, teamData);
                } else {
                    await createTeam(teamData);
                }
                resetForm();
                setEditingTeam(null);
                setDrawerOpen(false);
                fetchTeams();
            } catch (error) {
                // Error handling is done in individual functions
            } finally {
                setIsLoading(false);
            }
        }
    });

    const fetchUsers = async () => {
        try {
            const response = await apiInstance.get('/users-viewset?short_details=true');
            setUsers(response.data?.data || []);
        } catch (error) {
            showErrorToast('Error fetching users');
        }
    };

    const fetchCountries = async () => {
        try {
            const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/location?`;
            const response = await apiInstance.get(apiUrl);
            setCountries(response.data?.data || []);
        } catch (error) {
            showErrorToast('Error fetching countries');
        }
    };

    const fetchTeams = async () => {
        try {
            const response = await apiInstance.get('/teams/');
            setTeams(response.data?.data || []);
        } catch (error) {
            showErrorToast('Error fetching teams');
        }
    };

    const createTeam = async (teamData) => {
        try {
            const response = await apiInstance.post('/teams/', teamData);
            showToast(response.data?.message || 'Team created successfully');
            return response.data;
        } catch (error) {
            if (error.response?.data?.errors?.detail) {
                // Country assignment error
                setCountryError(error.response.data.errors.detail);
            } else if (error.response?.data?.errors?.name?.[0]?.includes('already exists')) {
                showErrorToast('Team with this name already exists. Please choose a different name.');
            } else {
                showErrorToast('Error creating team');
            }
            throw error;
        }
    };

    const updateTeam = async (teamId, teamData) => {
        try {
            const response = await apiInstance.put(`/teams/${teamId}/`, teamData);
            showToast(response.data?.message || 'Team updated successfully');
            return response.data;
        } catch (error) {
            if (error.response?.data?.errors?.detail) {
                // Country assignment error
                setCountryError(error.response.data.errors.detail);
            } else if (error.response?.data?.errors?.name?.[0]?.includes('already exists')) {
                showErrorToast('Team with this name already exists. Please choose a different name.');
            } else {
                showErrorToast('Error updating team');
            }
            throw error;
        }
    };

    const deleteTeam = async (teamId) => {
        try {
            const response = await apiInstance.delete(`/teams/${teamId}/`);
            showToast(response.data?.message || 'Team deleted successfully');
            fetchTeams();
            setDeleteDialog({ open: false, team: null });
        } catch (error) {
            showErrorToast('Error deleting team');
        }
    };

    const handleDeleteClick = (team) => {
        setDeleteDialog({ open: true, team });
    };

    const handleDeleteConfirm = () => {
        if (deleteDialog.team) {
            deleteTeam(deleteDialog.team.id);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ open: false, team: null });
    };

    const handleEdit = (team) => {
        setEditingTeam(team);
        setCountryError(''); // Clear any existing country errors
        const memberIds = team.team_members?.map(member => member.user_id || member.user) || [];
        const countryIds = team.country?.map(c => c.id) || [];
        formik.setValues({
            name: team.name,
            lead: team.lead || '',
            members: memberIds,
            countries: countryIds || []
        });
        setDrawerOpen(true);
    };

    useEffect(() => {
        fetchUsers();
        fetchTeams();
        fetchCountries();
    }, []);

    const handleCancel = () => {
        setEditingTeam(null);
        formik.resetForm();
        setDrawerOpen(false);
        setCountryError('');
    };

    const handleOpenDrawer = () => {
        setDrawerOpen(true);
        setCountryError('');
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setEditingTeam(null);
        formik.resetForm();
        setCountryError('');
    };

    const { errors, touched, values, handleSubmit, getFieldProps, setFieldValue } = formik;

    // Check if team name should be disabled when editing specific teams
    const isTeamNameDisabled = editingTeam && (
        editingTeam.name === 'Team Comm/Legal DD' || 
        editingTeam.name === 'Team Tech DD'
    );

    return (
        <Page title="Teams">
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4">
                        Team Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenDrawer}
                        sx={{
                            backgroundColor: '#00AB55',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: '#007B55' }
                        }}
                    >
                        Create New Team
                    </Button>
                </Box>

                <Typography variant="h6" sx={{ mb: 2 }}>
                    Teams
                </Typography>
                
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                    {teams.map((team) => (
                        <Card key={team.id}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <People color="primary" />
                                        {team.name}
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        <IconButton size="small" onClick={() => handleEdit(team)}>
                                            <Edit />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(team)}>
                                            <Delete />
                                        </IconButton>
                                    </Stack>
                                </Stack>

                                <Box>
                                    {/* Team Lead Section */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Team Lead:
                                        </Typography>
                                        {team.lead_name ? (
                                            <Chip
                                                key={team.lead_name}
                                                avatar={<Avatar sx={{ width: 24, height: 24 }}>{team.lead_name.charAt(0)}</Avatar>}
                                                label={team.lead_name}
                                                size="small"
                                                sx={{ 
                                                    backgroundColor: "#e3f2fd",
                                                    fontWeight: 500,
                                                    border: '1px solid #2196f3'
                                                }}
                                            />
                                        ) : <Typography variant="body2" color="text.secondary">No lead assigned</Typography>}
                                    </Box>

                                    {/* Team Members Section */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Members ({team.member_names?.length || 0}):
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {team.member_names?.map((member, index) => (
                                                <Chip
                                                    key={index}
                                                    avatar={<Avatar sx={{ width: 24, height: 24 }}>{member.charAt(0)}</Avatar>}
                                                    label={member}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )) || <Typography variant="body2" color="text.secondary">No members assigned</Typography>}
                                        </Box>
                                    </Box>

                                    {/* Countries Section */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Countries ({team.country?.length || 0}):
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {team.country?.length > 0 ? (
                                                (() => {
                                                    const maxVisible = 6;
                                                    const total = team.country?.length || 0;
                                                    const visibleCountries = team.country.slice(0, maxVisible);
                                                    const hiddenCountries = total > maxVisible ? team.country.slice(maxVisible) : [];

                                                    return (
                                                        <>
                                                            {visibleCountries.map((country, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    icon={<LocationOn sx={{ fontSize: 16 }} />}
                                                                    label={country.label || country.name}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: '#00AB55',
                                                                        color: 'white',
                                                                        fontWeight: 500,
                                                                        '& .MuiChip-icon': {
                                                                            color: 'white'
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                            {hiddenCountries.length > 0 && (
                                                                <Tooltip
                                                                    // title={hiddenCountries
                                                                    //     .map((c) => c.label || c.name)
                                                                    //     .join(', ')
                                                                    // }
                                                                    title={(
                                                                        <Box
                                                                            sx={{
                                                                                maxHeight: '40vh',
                                                                                overflowY: 'auto',
                                                                                display: 'flex',
                                                                                flexWrap: 'wrap',
                                                                                gap: 0.5
                                                                            }}
                                                                        >
                                                                            {hiddenCountries.map((country, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    icon={<LocationOn sx={{ fontSize: 16 }} />}
                                                                                    label={country.label || country.name}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: '#00AB55',
                                                                                        color: 'white',
                                                                                        fontWeight: 500,
                                                                                        '& .MuiChip-icon': {
                                                                                            color: 'white'
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    )}
                                                                >
                                                                    <Chip
                                                                        label={`+${hiddenCountries.length} more`}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    );
                                                })()
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">No countries assigned</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {teams.length === 0 && (
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <People sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No teams created yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Create your first team to get started
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {/* Drawer for Create/Edit Team */}
                <Drawer
                    anchor="right"
                    open={drawerOpen}
                    onClose={handleCloseDrawer}
                    PaperProps={{
                        sx: { width: '25%', minWidth: 400 }
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                {editingTeam ? 'Edit Team' : 'Create New Team'}
                            </Typography>
                            <IconButton onClick={handleCloseDrawer} size="small">
                                <Close />
                            </IconButton>
                        </Box>
                        <Divider sx={{ mb: 3 }} />

                        <FormikProvider value={formik}>
                            <Form onSubmit={handleSubmit}>
                                <Stack spacing={3}>
                                    {/* Team Name */}
                                    <TextField
                                        label="Team Name"
                                        {...getFieldProps('name')}
                                        error={Boolean(touched.name && errors.name)}
                                        helperText={touched.name && errors.name}
                                        fullWidth
                                        disabled={isTeamNameDisabled}
                                    />
                                    
                                    {/* Team Lead */}
                                    <Autocomplete
                                        fullWidth
                                        options={users}
                                        getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                                        value={users.filter(user => user.pk === values.lead)[0] || null}
                                        onChange={(event, value) => {
                                            const leadId = value ? value.pk : '';
                                            setFieldValue('lead', leadId);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Team Lead"
                                                error={Boolean(touched.lead && errors.lead)}
                                                helperText={touched.lead && errors.lead}
                                            />
                                        )}
                                    />

                                    {/* Team Members */}
                                    <Autocomplete
                                        fullWidth
                                        multiple
                                        options={users}
                                        getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                                        value={users.filter(user => values.members.includes(user.pk))}
                                        onChange={(event, value) => {
                                            const memberIds = value.map(user => user.pk);
                                            setFieldValue('members', memberIds);
                                        }}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option.pk}
                                                    avatar={<Avatar sx={{ width: 24, height: 24 }}>{option.first_name.charAt(0)}</Avatar>}
                                                    label={`${option.first_name} ${option.last_name}`}
                                                    size="small"
                                                />
                                            ))
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Team Members"
                                                error={Boolean(touched.members && errors.members)}
                                                helperText={touched.members && errors.members}
                                                placeholder="Select team members"
                                            />
                                        )}
                                    />

                                    {/* Countries */}
                                    <Box>
                                        <Autocomplete
                                            fullWidth
                                            multiple
                                            options={countries}
                                            getOptionLabel={(option) => option.name || ''}
                                            value={countries.filter(c => values.countries.includes(c.id))}
                                            onChange={(event, value) => {
                                                const ids = value.map(c => c.id);
                                                setFieldValue('countries', ids);
                                                setCountryError(''); // Clear error when user changes selection
                                            }}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        {...getTagProps({ index })}
                                                        key={option.id}
                                                        avatar={
                                                            <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                                                                <LocationOn sx={{ color: 'white', fontSize: 14 }} />
                                                            </Avatar>
                                                        }
                                                        label={option.name}
                                                        size="small"
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Countries (Optional)"
                                                    error={Boolean((touched.countries && errors.countries) || countryError)}
                                                    helperText={touched.countries && errors.countries}
                                                    placeholder="Select countries (optional)"
                                                />
                                            )}
                                        />
                                        {countryError && (
                                            <FormHelperText error sx={{ mx: 1.75, mt: 0.5 }}>
                                                {countryError}
                                            </FormHelperText>
                                        )}
                                    </Box>
                                
                                    {/* Actions Buttons */}
                                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={handleCancel}
                                            sx={{ minWidth: 100 }}
                                        >
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            type="submit"
                                            variant="contained"
                                            loading={isLoading}
                                            startIcon={<Add />}
                                            sx={{ 
                                                minWidth: 120,
                                                backgroundColor: '#00AB55',
                                                '&:hover': { backgroundColor: '#007B55' }
                                            }}
                                        >
                                            {editingTeam ? 'Update' : 'Create'}
                                        </LoadingButton>
                                    </Stack>
                                </Stack>
                            </Form>
                        </FormikProvider>
                    </Box>
                </Drawer>

                <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
                    <DialogTitle>Delete Team</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete the team "{deleteDialog.team?.name}"? 
                            This action cannot be undone and will permanently remove the team and all its members.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Page>
    );
}